// The day, as time rather than as a list.
//
// Everything else in this console answers *where* — a map, a board, a
// column sorted by who needs the dispatcher. None of it answers the
// question a dispatcher actually plans around: **will this finish?**
// A ranked list cannot say that. A clock can.
//
// One lane per round, all against one axis:
//
//   sent ──────────── last heard ┄┄┄ now ═══ projected finish
//
// What is drawn is exactly what is known, and no more:
//
//   * `sent` is this console's own record of when it sealed the job.
//   * `last heard` is the newest record's own timestamp (§5.2 field 2).
//   * the projection is `now + driving seconds`, routed door to door by
//     the same engine the arrival times come from — and it exists **only
//     while the run has a live position**, the same gate the map pin
//     obeys. No fix, no projection; the lane simply stops at now.
//
// Two things are deliberately absent. There are no per-stop ticks,
// because `outcomes` is cumulative and carries no timestamps — a mark
// placed at a plausible time would be the one invention on a surface
// whose whole argument is that it does not invent. And the projection is
// **driving time only**: nobody has told this console how long a courier
// spends at a door, so the label says "driving" everywhere it appears.

import type { FollowSnapshot } from "@wayfind/core";
import { STOP_OUTCOME, stopProgress } from "@wayfind/core";
import type { StoredJob } from "../ops/state";
import type { RemainingRoad } from "../ops/leg";
import type { Vocabulary } from "../ops/words";
import { nowSeconds } from "../config";

const HOUR = 3600;

export interface DayLane {
  job: StoredJob;
  snapshot: FollowSnapshot | null;
  road: RemainingRoad | undefined;
  who: string;
}

/** Where a lane's marks fall on the shared axis, in seconds. */
interface Span {
  sent: number;
  heard: number | null;
  finish: number | null;
  stale: boolean;
  trouble: boolean;
}

function spanOf(lane: DayLane, now: number): Span {
  const status = lane.snapshot?.status ?? null;
  const seconds = lane.road?.secondsRemaining ?? null;
  return {
    sent: lane.job.createdUnixSeconds,
    heard: status?.observed.atUnixSeconds ?? null,
    // A projection needs a live position, which is what produced the
    // road in the first place — so this is null exactly when the pin is.
    finish: seconds == null ? null : now + seconds,
    stale: status?.observed.stale ?? false,
    trouble: (status?.outcomes ?? []).some(
      outcome => outcome === STOP_OUTCOME.SKIPPED || outcome === STOP_OUTCOME.FAILED
    )
  };
}

function clockAt(unixSeconds: number): string {
  return new Date(unixSeconds * 1000).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit"
  });
}

function saySeconds(seconds: number): string {
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
}

export function DayBar({
  lanes,
  selected,
  vocabulary,
  onSelect,
  onOpen
}: {
  lanes: DayLane[];
  selected: string | null;
  vocabulary: Vocabulary;
  onSelect(id: string): void;
  onOpen(job: StoredJob): void;
}): JSX.Element | null {
  const now = nowSeconds();
  if (!lanes.length) return null;

  const spans = lanes.map(lane => spanOf(lane, now));

  // The window holds everything there is to draw, rounded out to whole
  // hours so the ruler lands on times a person recognises.
  const earliest = Math.min(...spans.map(span => span.sent), now);
  const latest = Math.max(now, ...spans.map(span => span.finish ?? now));
  const start = Math.floor(earliest / HOUR) * HOUR;
  // At least two hours wide: a round sent four minutes ago should not
  // render as a full-width bar because it is the only thing on the axis.
  const end = Math.max(Math.ceil(latest / HOUR) * HOUR, start + 2 * HOUR);
  const width = end - start;
  const at = (unixSeconds: number): number =>
    Math.max(0, Math.min(100, ((unixSeconds - start) / width) * 100));

  const hours: number[] = [];
  for (let tick = start; tick <= end; tick += HOUR) hours.push(tick);

  return (
    <div className="daybar">
      <div className="daybar__head">
        <span className="daybar__headlabel">The day</span>
        <span className="daybar__headnote">
          Projections are driving time from the last position, and appear only while a
          round is publishing one.
        </span>
      </div>

      <div className="daybar__grid">
        {/* The ruler, and the gridlines every lane is read against. */}
        <div className="daybar__ruler" aria-hidden="true">
          {hours.map(tick => (
            <span key={tick} className="daybar__hour" style={{ insetInlineStart: `${at(tick)}%` }}>
              {clockAt(tick)}
            </span>
          ))}
        </div>

        <div className="daybar__lanes">
          <div className="daybar__rules" aria-hidden="true">
            {hours.map(tick => (
              <i key={tick} className="daybar__rule" style={{ insetInlineStart: `${at(tick)}%` }} />
            ))}
            <i className="daybar__now" style={{ insetInlineStart: `${at(now)}%` }} />
          </div>

          {lanes.map((lane, index) => {
            const span = spans[index]!;
            const isSelected = selected === lane.job.id;
            const progress = lane.snapshot
              ? stopProgress(lane.snapshot.status, lane.snapshot.plan)
              : null;
            const done = progress?.delivered ?? 0;
            const total = progress?.total ?? lane.job.stops.length;
            const tone = span.trouble ? "halt" : span.stale ? "quiet" : span.finish ? "live" : "idle";

            return (
              <button
                key={lane.job.id}
                type="button"
                className={`daylane daylane--${tone}${isSelected ? " daylane--on" : ""}`}
                aria-label={
                  `${lane.who}: sent ${clockAt(span.sent)}, ${done} of ${total} delivered`
                  + (span.finish
                    ? `, about ${saySeconds(span.finish - now)} driving left`
                    : ", no projection — not publishing a position")
                }
                onClick={() => onSelect(lane.job.id)}
                onDoubleClick={() => onOpen(lane.job)}
              >
                <span className="daylane__who">{lane.who}</span>

                <span className="daylane__track">
                  {/* The lane itself, so a round sent four minutes ago
                      still reads as a place on a timeline rather than as
                      an empty row. */}
                  <i className="daylane__rail" />
                  {/* When this console sealed the job. Its own record, and
                      the one mark on the lane that is never an estimate. */}
                  <i className="daylane__sent" style={{ insetInlineStart: `${at(span.sent)}%` }} />
                  {/* What has already happened: sent until the last thing
                      the run said, or until now if it never said anything. */}
                  <i
                    className="daylane__done"
                    style={{
                      insetInlineStart: `${at(span.sent)}%`,
                      inlineSize: `${Math.max(at(span.heard ?? now) - at(span.sent), 0.4)}%`
                    }}
                  />
                  {/* Silence, drawn as silence: the gap between the last
                      record and now, which is the thing a dispatcher finds
                      out about too late. */}
                  {span.heard && span.stale ? (
                    <i
                      className="daylane__gap"
                      style={{
                        insetInlineStart: `${at(span.heard)}%`,
                        inlineSize: `${Math.max(at(now) - at(span.heard), 0)}%`
                      }}
                    />
                  ) : null}
                  {/* What is left to drive. Amber only on the selected
                      round — the same scarcity rule the map obeys. */}
                  {span.finish ? (
                    <i
                      className="daylane__left"
                      style={{
                        insetInlineStart: `${at(now)}%`,
                        inlineSize: `${Math.max(at(span.finish) - at(now), 0.4)}%`
                      }}
                    />
                  ) : null}
                </span>

                <span className="daylane__facts">
                  <span className="daylane__count wf-numeric">{done}/{total}</span>
                  <span className="daylane__eta">
                    {span.finish
                      ? `${saySeconds(span.finish - now)} driving`
                      : span.stale
                        ? "gone quiet"
                        : lane.snapshot?.status.observed.ever ? "no position" : "not started"}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <p className="daybar__foot small muted">
        A lane stops at now when its round is not publishing a position — there is nothing
        to project from, and a guess would be the one invented thing on this screen.
        {" "}{spans.some(span => span.trouble)
          ? `A clay lane has a ${vocabulary.stop} that did not get delivered.`
          : ""}
      </p>
    </div>
  );
}
